import React, { useState } from 'react';
import { COUNTRIES } from '../../data/countries';
import { LLM_API_KEY } from '../../config';
import * as pdfjsLib from 'pdfjs-dist';
import Tesseract from 'tesseract.js';
import EmailGateModal from '../freemium/EmailGateModal';

// Set worker locally
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

function cleanJSON(raw) {
  let s = raw.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '').trim();
  const m = s.match(/\{[\s\S]*\}/);
  return m ? m[0] : s;
}

// Extract text from PDF — tries text layer first, falls back to OCR
async function extractTextFromPDF(file, onProgress) {
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  let fullText = '';

  // Try text extraction first
  for (let i = 1; i <= Math.min(pdf.numPages, 5); i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    fullText += content.items.map(item => item.str).join(' ') + '\n';
  }

  if (fullText.trim().length > 50) {
    console.log('[PDF] Text extracted:', fullText.length, 'chars');
    return fullText.trim().substring(0, 4000);
  }

  // No text → scanned PDF → OCR via rendering to canvas
  console.log('[PDF] No text found, running OCR...');
  if (onProgress) onProgress('OCR en cours...');
  let ocrText = '';

  for (let i = 1; i <= Math.min(pdf.numPages, 3); i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    const ctx = canvas.getContext('2d');
    await page.render({ canvasContext: ctx, viewport }).promise;

    const { data: { text } } = await Tesseract.recognize(canvas, 'fra+eng+ara', {
      logger: m => { if (m.status === 'recognizing text' && onProgress) onProgress(`OCR page ${i}... ${Math.round(m.progress * 100)}%`); }
    });
    ocrText += text + '\n';
  }

  console.log('[PDF] OCR extracted:', ocrText.length, 'chars');
  return ocrText.trim().substring(0, 4000) || `[PDF illisible même après OCR: ${file.name}]`;
}

async function extractText(file, onProgress) {
  if (file.type === 'application/pdf') {
    try {
      return await extractTextFromPDF(file, onProgress);
    } catch (e) {
      console.error('PDF extraction error:', e);
      return `[Erreur extraction PDF: ${file.name} — ${e.message}]`;
    }
  }
  // For images: convert to base64 and send to DeepSeek for OCR
  if (file.type.startsWith('image/')) {
    const base64 = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.readAsDataURL(file);
    });
    try {
      const resp = await fetch('https://api.deepseek.com/v1/chat/completions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${LLM_API_KEY}` },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [{ role: 'user', content: `Extrais tout le texte visible de ce document commercial/douanier. Le document est une image encodée en base64. Nom du fichier: ${file.name}. Retourne uniquement le texte extrait, sans commentaire.` }],
          temperature: 0,
        }),
      });
      const d = await resp.json();
      return d.choices?.[0]?.message?.content || `[Image: ${file.name}]`;
    } catch { return `[Image: ${file.name}]`; }
  }
  return `[Fichier: ${file.name}]`;
}

async function verifyDocument(docType, extractedText, product, hsCode, country, lang) {
  const L = lang === 'ar' ? 'ARABE' : 'FRANÇAIS';
  const prompt = `Tu es un expert douanier algérien. Vérifie la conformité COMPLÈTE de ce document.

TYPE DE DOCUMENT : ${docType}
PRODUIT : ${product}
CODE SH : ${hsCode}
PAYS FOURNISSEUR : ${country}
TEXTE EXTRAIT DU DOCUMENT :
${extractedText}

RÈGLES IMPORTANTES :
1. Vérifie TOUTES les exigences, même si le document est dans une mauvaise langue
2. Ne t'arrête PAS à la première erreur — liste TOUS les problèmes d'un coup
3. Si le document est en anglais ou autre langue que français/arabe, c'est une anomalie à signaler MAIS continue quand même à vérifier tout le contenu
4. Vérifie : langue, mentions obligatoires, cohérence des données, signatures, cachets, dates, codes SH, incoterms, pays d'origine, quantités, prix

Réponds en ${L}. JSON UNIQUEMENT :
{
  "documentType": "${docType}",
  "overallStatus": "CONFORME|ANOMALIES_MINEURES|ANOMALIES_CRITIQUES|ILLISIBLE",
  "checks": [
    {"requirement":"exigence vérifiée","status":"OK|MANQUANT|INCORRECT|NON_VERIFIE","found":"ce qui a été trouvé (ou null)","expected":"ce qui était attendu","severity":"CRITIQUE|IMPORTANT|MINEUR","correctionNeeded":"correction précise à demander"}
  ],
  "globalNotes": "résumé global du document",
  "actionRequired": "actions immédiates si anomalies critiques"
}`;

  const resp = await fetch('https://api.deepseek.com/v1/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${LLM_API_KEY}` },
    body: JSON.stringify({ model: 'deepseek-chat', messages: [{ role: 'system', content: prompt }, { role: 'user', content: 'Vérifie ce document de manière exhaustive. Liste TOUS les problèmes.' }], temperature: 0.1 }),
  });
  if (!resp.ok) throw new Error(`API ${resp.status}`);
  const d = await resp.json();
  return JSON.parse(cleanJSON(d.choices[0].message.content));
}

const DOC_TYPES_FR = [
  'Facture commerciale','Liste de colisage','Connaissement (Bill of Lading)','Certificat d\'origine',
  'EUR.1','Certificat GZALE','Fiche technique','Certificat sanitaire vétérinaire','Certificat halal',
  'Certificat phytosanitaire','Certificat d\'analyse laboratoire','Certificat HACCP/ISO 22000',
  'Certificat BPF/GMP','Fiche de Données de Sécurité (FDS)','Certificat de conformité CE',
  'Homologation ARPCE','Mill Test Certificate','Autre',
];
const DOC_TYPES_AR = [
  'الفاتورة التجارية','قائمة التعبئة','سند الشحن','شهادة المنشأ',
  'EUR.1','شهادة GZALE','البطاقة التقنية','الشهادة الصحية البيطرية','شهادة حلال',
  'الشهادة الصحية النباتية','شهادة تحليل مخبري','شهادة HACCP/ISO 22000',
  'شهادة GMP','صحيفة بيانات السلامة','شهادة المطابقة CE',
  'مصادقة ARPCE','شهادة الجودة MTC','أخرى',
];

const STATUS_ICONS = { CONFORME: '✅', ANOMALIES_MINEURES: '⚠️', ANOMALIES_CRITIQUES: '🚨', ILLISIBLE: '❓' };
const STATUS_COLORS = { CONFORME: 'rgba(16,185,129,0.15)', ANOMALIES_MINEURES: 'rgba(245,158,11,0.15)', ANOMALIES_CRITIQUES: 'rgba(239,68,68,0.15)', ILLISIBLE: 'rgba(148,163,184,0.1)' };
const CHECK_ICONS = { OK: '✅', MANQUANT: '🚨', INCORRECT: '⚠️', NON_VERIFIE: '❓' };

export default function VerifyModule({ lang }) {
  const ar = lang === 'ar';
  const L = (fr, a) => ar ? a : fr;
  const docTypes = ar ? DOC_TYPES_AR : DOC_TYPES_FR;

  const [step, setStep] = useState(1);
  const [context, setContext] = useState({ product: '', hsCode: '', country: '' });
  const [files, setFiles] = useState([]); // [{file, docType, id}]
  const [results, setResults] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showGate, setShowGate] = useState(false);

  if (false) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔒</div>
        <h3>{L('Vérification documentaire', 'التحقق من الوثائق')}</h3>
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem', margin: '0.5rem 0 1rem' }}>
          {L('Cette fonctionnalité nécessite un email vérifié.', 'هذه الميزة تتطلب بريداً إلكترونياً مُتحققاً منه.')}
        </p>
        <button className="btn btn-primary" style={{ width: 'auto' }} onClick={() => setShowGate(true)}>
          {L('Vérifier mon email', 'تحقق من بريدي')}
        </button>
        {showGate && <EmailGateModal lang={lang} onVerified={() => { setShowGate(false); window.location.reload(); }} />}
      </div>
    );
  }

  const addFiles = (newFiles) => {
    const added = Array.from(newFiles).filter(f => f.size <= 10 * 1024 * 1024).slice(0, 10 - files.length).map(f => ({
      file: f, docType: '', id: crypto.randomUUID(),
    }));
    setFiles(prev => [...prev, ...added]);
  };

  const removeFile = (id) => setFiles(prev => prev.filter(f => f.id !== id));
  const setDocType = (id, type) => setFiles(prev => prev.map(f => f.id === id ? { ...f, docType: type } : f));

  const verify = async () => {
    if (files.length === 0 || !context.product) return;
    // Check doc verification limit
    const { canVerifyDocs, incrementDocVerify } = await import('../../lib/freemium');
    const allowed = canVerifyDocs(null);
    if (allowed !== true) { setShowGate(true); return; }
    incrementDocVerify();
    setVerifying(true); setProgress(0); setResults(null);
    const res = [];
    for (let i = 0; i < files.length; i++) {
      setProgress(Math.round(((i) / files.length) * 100));
      const f = files[i];
      try {
        const text = await extractText(f.file, (msg) => setProgress(prev => { /* update status */ return prev; }));
        const result = await verifyDocument(f.docType || f.file.name, text, context.product, context.hsCode, context.country, lang);
        res.push({ ...result, fileName: f.file.name });
      } catch (err) {
        res.push({ fileName: f.file.name, overallStatus: 'ILLISIBLE', checks: [], globalNotes: err.message, actionRequired: '' });
      }
    }
    setProgress(100);
    setResults(res);
    // Save to Supabase
    try {
      const { db } = await import('../../lib/supabase');
      const userEmail = localStorage.getItem('tidjarai_user_email') || null;
      await db.from('verification_sessions').insert({
        product_description: context.product, hs_code: context.hsCode,
        origin_country: context.country, status: 'completed',
        results: res, user_email: userEmail,
      });
    } catch {}
    setVerifying(false);
    setStep(4);
  };

  const generateCorrectionEmail = () => {
    if (!results) return '';
    const issues = results.flatMap(r => r.checks?.filter(c => c.status !== 'OK').map(c => `- ${r.fileName}: ${c.requirement} → ${c.correctionNeeded}`) || []);
    return `Subject: Corrections required on documents — ${context.product}\n\nDear Sir/Madam,\n\nFollowing our review of the documents for the shipment of ${context.product} (HS Code: ${context.hsCode}), we have identified the following issues that need to be corrected before we can proceed with customs clearance in Algeria:\n\n${issues.join('\n')}\n\nPlease send corrected documents at your earliest convenience.\n\nBest regards,\n[Your company name]`;
  };

  return (
    <div>
      <div className="card">
        <div className="card-title">🔍 {L('Vérification documentaire par IA', 'التحقق من الوثائق بالذكاء الاصطناعي')}</div>

        {/* Step indicators */}
        <div style={{ display: 'flex', gap: '0.3rem', marginBottom: '1rem' }}>
          {[1, 2, 3, 4].map(s => (
            <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: step >= s ? '#6366f1' : '#e5e7eb' }} />
          ))}
        </div>

        {/* Step 1: Context */}
        {step === 1 && (<>
          <h4 style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>{L('1. Contexte de l\'opération', '1. سياق العملية')}</h4>

          {/* Load from history */}
          {(() => {
            try { const h = JSON.parse(localStorage.getItem('tidjarai_history') || '[]'); if (h.length === 0) return null;
              return (<div style={{ marginBottom: '0.8rem' }}>
                <label style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', display: 'block', marginBottom: '0.2rem' }}>{L('📋 Charger depuis une analyse précédente :', '📋 تحميل من تحليل سابق:')}</label>
                <select onChange={e => { const item = h[e.target.value]; if (item) setContext({ product: item.description, hsCode: item.code_sh, country: item.pays }); }} style={{ width: '100%', padding: '0.5rem', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: '0.82rem' }}>
                  <option value="">{L('— Sélectionner —', '— اختر —')}</option>
                  {h.map((item, i) => <option key={i} value={i}>{item.description} — {item.code_sh}</option>)}
                </select>
              </div>);
            } catch { return null; }
          })()}

          <div className="form-group"><label>{L('Produit', 'المنتج')} *</label><input value={context.product} onChange={e => setContext({ ...context, product: e.target.value })} placeholder={L('Ex: câbles électriques', 'مثال: كابلات كهربائية')} /></div>
          <div className="form-row">
            <div className="form-group"><label>{L('Code SH', 'رمز SH')}</label><input value={context.hsCode} onChange={e => setContext({ ...context, hsCode: e.target.value })} placeholder="8544.49.90.00" /></div>
            <div className="form-group"><label>{L('Pays fournisseur', 'بلد المورد')}</label>
              <select value={context.country} onChange={e => setContext({ ...context, country: e.target.value })}>
                <option value="">{L('— Sélectionner —', '— اختر —')}</option>
                {COUNTRIES.map(c => <option key={c.code} value={c.code}>{ar ? c.ar : c.name}</option>)}
              </select>
            </div>
          </div>
          <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => context.product && setStep(2)}>
            {L('Suivant →', 'التالي →')}
          </button>
        </>)}

        {/* Step 2: Upload */}
        {step === 2 && (<>
          <h4 style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>{L('2. Uploadez vos documents', '2. ارفع وثائقك')}</h4>
          <div
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#6366f1'; }}
            onDragLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
            onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#e5e7eb'; addFiles(e.dataTransfer.files); }}
            onClick={() => document.getElementById('file-input').click()}
            style={{ border: '2px dashed #e5e7eb', borderRadius: 12, padding: '2rem', textAlign: 'center', cursor: 'pointer', marginBottom: '1rem' }}>
            <div style={{ fontSize: '1.5rem' }}>📄</div>
            <p style={{ fontSize: '0.82rem', color: 'var(--muted)' }}>{L('Glissez vos fichiers ici ou cliquez (PDF, JPG, PNG — max 10MB)', 'اسحب ملفاتك هنا أو انقر (PDF، JPG، PNG — حد أقصى 10 ميغابايت)')}</p>
            <input id="file-input" type="file" multiple accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }} onChange={e => addFiles(e.target.files)} />
          </div>
          {files.map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0', borderBottom: '1px solid #f3f4f6', fontSize: '0.82rem' }}>
              <span>📎</span>
              <span style={{ flex: 1 }}>{f.file.name} <span style={{ color: 'var(--muted)', fontSize: '0.7rem' }}>({(f.file.size / 1024).toFixed(0)} KB)</span></span>
              <button onClick={() => removeFile(f.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}>✕</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button className="btn btn-reset" onClick={() => setStep(1)}>← {L('Retour', 'رجوع')}</button>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => files.length > 0 && setStep(3)} disabled={files.length === 0}>
              {L('Suivant →', 'التالي →')}
            </button>
          </div>
        </>)}

        {/* Step 3: Associate types */}
        {step === 3 && (<>
          <h4 style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>{L('3. Identifiez chaque document', '3. حدد كل وثيقة')}</h4>
          {files.map(f => (
            <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6' }}>
              <span style={{ fontSize: '0.82rem', flex: 1 }}>📎 {f.file.name}</span>
              <select value={f.docType} onChange={e => setDocType(f.id, e.target.value)} style={{ padding: '0.4rem', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: '0.78rem' }}>
                <option value="">{L('— Type de document —', '— نوع الوثيقة —')}</option>
                {docTypes.map((dt, i) => <option key={i} value={dt}>{dt}</option>)}
              </select>
            </div>
          ))}
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button className="btn btn-reset" onClick={() => setStep(2)}>← {L('Retour', 'رجوع')}</button>
            <button className="btn btn-primary" style={{ flex: 1 }} onClick={verify} disabled={verifying}>
              {verifying ? <><span className="spinner" /> {L(`Vérification... ${progress}%`, `التحقق... ${progress}%`)}</> : L('🔍 Vérifier mes documents', '🔍 تحقق من وثائقي')}
            </button>
          </div>
        </>)}

        {/* Step 4: Results */}
        {step === 4 && results && (<>
          <h4 style={{ fontSize: '0.85rem', marginBottom: '0.8rem' }}>{L('4. Rapport de conformité', '4. تقرير المطابقة')}</h4>
          {results.map((r, i) => (
            <div key={i} style={{ marginBottom: '1rem', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.6rem 0.8rem', background: STATUS_COLORS[r.overallStatus] || '#f3f4f6' }}>
                <span style={{ fontWeight: 600, fontSize: '0.82rem' }}>📎 {r.fileName}</span>
                <span style={{ fontSize: '0.82rem' }}>{STATUS_ICONS[r.overallStatus]} {r.overallStatus?.replace(/_/g, ' ')}</span>
              </div>
              {r.checks?.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.76rem' }}>
                  <thead><tr style={{ background: 'rgba(124,58,237,0.15)' }}>
                    <th style={{ padding: '0.4rem', textAlign: ar ? 'right' : 'left' }}>{L('Exigence', 'المتطلب')}</th>
                    <th style={{ padding: '0.4rem', width: 40, textAlign: 'center' }}></th>
                    <th style={{ padding: '0.4rem', textAlign: ar ? 'right' : 'left' }}>{L('Trouvé', 'الموجود')}</th>
                    <th style={{ padding: '0.4rem', textAlign: ar ? 'right' : 'left' }}>{L('Correction', 'التصحيح')}</th>
                  </tr></thead>
                  <tbody>{r.checks.map((c, j) => (
                    <tr key={j} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '0.35rem 0.4rem' }}>{c.requirement}</td>
                      <td style={{ textAlign: 'center' }}>{CHECK_ICONS[c.status]}</td>
                      <td style={{ padding: '0.35rem 0.4rem', color: 'var(--muted)' }}>{c.found || '—'}</td>
                      <td style={{ padding: '0.35rem 0.4rem', color: c.severity === 'CRITIQUE' ? '#dc2626' : '#d97706', fontWeight: c.severity === 'CRITIQUE' ? 600 : 400 }}>{c.correctionNeeded || '—'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              )}
              {r.actionRequired && <div style={{ padding: '0.5rem 0.8rem', background: '#fef2f2', fontSize: '0.78rem', color: '#dc2626', fontWeight: 600 }}>⚠️ {r.actionRequired}</div>}
            </div>
          ))}

          {/* Summary */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            {(() => {
              const ok = results.flatMap(r => r.checks || []).filter(c => c.status === 'OK').length;
              const crit = results.flatMap(r => r.checks || []).filter(c => c.status === 'MANQUANT' || c.status === 'INCORRECT').filter(c => c.severity === 'CRITIQUE').length;
              const minor = results.flatMap(r => r.checks || []).filter(c => c.status !== 'OK').length - crit;
              return <>
                <span className="badge badge-green">{ok} {L('conformes', 'مطابق')}</span>
                {crit > 0 && <span className="badge badge-red">{crit} {L('critiques', 'حرجة')}</span>}
                {minor > 0 && <span className="badge badge-yellow">{minor} {L('mineures', 'ثانوية')}</span>}
              </>;
            })()}
          </div>

          <div className="downloads">
            <button className="btn btn-outline" onClick={() => { const w = window.open('', '_blank'); w.document.write('<pre>' + JSON.stringify(results, null, 2) + '</pre>'); w.print(); }}>
              📄 {L('Télécharger rapport PDF', 'تحميل التقرير PDF')}
            </button>
            <button className="btn btn-outline" onClick={() => { navigator.clipboard.writeText(generateCorrectionEmail()); }}>
              ✉️ {L('Copier email correction fournisseur', 'نسخ بريد التصحيح للمورد')}
            </button>
          </div>

          <button className="btn btn-reset" style={{ marginTop: '0.8rem', width: '100%' }} onClick={() => { setStep(1); setResults(null); setFiles([]); }}>
            ↺ {L('Nouvelle vérification', 'تحقق جديد')}
          </button>
        </>)}
      </div>
    </div>
  );
}
